import React from "react";

import ManageChats from "@editor/components/editor/ai/chat/ManageChats";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { fileVersionRouter } from "@editor/trpc/fileVersion";
import { nextJSProjectRouter } from "@editor/trpc/nextJSProject";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import ReactEditorChat from "@/features/react-editor/components/chat/ReactEditorChat";
// import DesignLibraryPanel from "@/features/react-editor/components/DesignLibraryPanel";
import ModifiablePageRenderer from "@/features/react-editor/components/ModifiablePageRenderer";
import { ModifiersProvider } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import ReactEditorGlobalHotkeysListener from "@/features/react-editor/components/ReactEditorGlobalHotkeysListener";
import { useChatSidebar } from "@/features/react-editor/hooks/useChatSidebar";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import Button from "@replo/design-system/components/button/Button";
import { MobileSheet } from "@replo/design-system/components/mobile-sheet/MobileSheet";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useSidebar } from "@replo/design-system/components/shadcn/core/sidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import { isSafari } from "@replo/design-system/utils/platform";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import DesignLibraryPanel from "./components/DesignLibraryPanel";
import VersionHistory from "./components/history/VersionHistory";
import LoadingHeader from "./components/LoadingHeader";
import MobileChatFooter from "./components/MobileChatFooter";
import PageRenderControls from "./components/PageRenderControls";
import PageSettingsPanel from "./components/PageSettingsPanel";
import {
  useEditorStoreActions,
  useMobileChatMode,
  usePanelView,
} from "./store/editorStore";
import {
  useSandboxStateActions,
  useViewingVersionId,
} from "./store/SandboxStateStoreContext";

/**
 * Listens for changes in the panel view, and if we go to chat, then auto-collapse the sidebar
 * so there's more space for the chat.
 *
 * Note (Noah, 2025-08-30): Kind of weird to have a component for this but it's
 * the only way to respond to chat changes and trigger sidebar changes, since
 * shadcn sidebar only exposes the toggleSidebar through a hook. I didn't do
 * this in the main chat component since that would mean it would rerender
 * unnecessarily when the sidebar changed.
 */
function ChatPanelSidebarListener() {
  const panelView = usePanelView();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;
  useOnValueChange(panelView, (newPanelView) => {
    if (newPanelView === "chat" && isSidebarExpanded) {
      toggleSidebar();
    }
  });
  return null;
}

function ReactEditor() {
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const panelView = usePanelView();

  // Create a ref for the main container to handle keyboard events
  const reactEditorContainerRef = React.useRef<HTMLDivElement>(null);
  const isUsingSafari = isSafari();
  const isUsingDaytona = isFeatureEnabled("use-server-sandbox");
  const [isShowingSafariWarning, setIsShowingSafariWarning] = React.useState(
    isUsingSafari && !isUsingDaytona,
  );

  const { data: page, isLoading: isPageLoading } =
    nextJSProjectRouter.getPage.useQuery(
      projectId ? { projectId, id: pageId ?? "" } : skipToken,
      {
        enabled: Boolean(projectId && pageId),
      },
    );

  if (!project) {
    return null;
  }

  const isWaitingForPageData = Boolean(pageId && isPageLoading);

  return (
    <>
      <Modal
        title="Replo AI builder works best in Chrome"
        description="We're working on adding support for Safari soon. If you're using Safari, please switch to Chrome to continue using the Replo AI builder, or you may see issues with pages not loading."
        isOpen={isShowingSafariWarning}
        onOpenChange={(open) => {
          if (!open) {
            setIsShowingSafariWarning(false);
          }
        }}
        footer={
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              setIsShowingSafariWarning(false);
            }}
          >
            Close
          </Button>
        }
      />

      <ReactEditorContent
        pageId={pageId}
        panelView={panelView}
        reactEditorContainerRef={reactEditorContainerRef}
        page={page}
        isWaitingForPageData={isWaitingForPageData}
      />
      <ChatPanelSidebarListener />
    </>
  );
}

function ReactEditorContent({
  pageId,
  panelView,
  reactEditorContainerRef,
  page,
  isWaitingForPageData,
}: {
  pageId: string | null;
  panelView: string;
  reactEditorContainerRef: React.RefObject<HTMLDivElement>;
  page: any;
  isWaitingForPageData: boolean;
}) {
  const [searchParams] = useSearchParams();
  const chatIdParam = searchParams.get("chatId");
  const projectId = useCurrentProjectId();
  const viewingVersionId = useViewingVersionId();
  const { setViewingVersionId } = useSandboxStateActions();
  const { setPanelView } = useEditorStoreActions();
  const currentViewport = useViewportStore((state) => state.currentViewport);
  const { open: isChatSidebarOpen } = useChatSidebar();

  const { data: fileVersionsData } = fileVersionRouter.findByFileId.useQuery(
    projectId && pageId
      ? {
          projectId,
          fileId: pageId,
          cursor: 0,
          limit: 1,
        }
      : skipToken,
  );

  const fileVersions = fileVersionsData?.fileVersions;

  if (fileVersions && fileVersions.length > 0 && viewingVersionId === null) {
    setViewingVersionId(fileVersions[0]?.id ?? null);
  }

  const isMobile = useIsMobile();

  const mobileChatMode = useMobileChatMode();
  const { setMobileChatMode } = useEditorStoreActions();

  const canPreview = Boolean(pageId);
  const effectiveMobileMode = canPreview ? mobileChatMode : "chat";

  useOnValueChange(chatIdParam, (current, previous) => {
    if (current === "" && previous !== "" && isMobile) {
      setMobileChatMode("chat");
    }
  });

  const panelContent = (
    <>
      {panelView === "chat" && <ReactEditorChat />}
      {panelView === "history" && <VersionHistory />}
      {panelView === "designLibrary" && <DesignLibraryPanel />}
      {panelView === "pageSettings" && page && (
        <PageSettingsPanel page={page} />
      )}
      {panelView === "manageChats" && <ManageChats />}
    </>
  );

  return (
    <ModifiersProvider>
      {/* NOTE (Kurt, 2025-07-17): We need to create a ref for the main container to handle keyboard events. Without
      this, hotkeys don't work because the events are captured by iframes or other elements.*/}
      <div
        ref={reactEditorContainerRef}
        className="flex flex-col md:flex-row md:gap-3 h-full w-full outline-none"
        tabIndex={-1}
      >
        <ReactEditorGlobalHotkeysListener
          containerRef={reactEditorContainerRef}
        />

        <div
          className={twMerge(
            "hidden md:flex h-full flex-col gap-5 min-h-0 min-w-0 items-stretch self-start",
            pageId === null && "md:flex-1",
            pageId && !isChatSidebarOpen && "md:basis-[33%]",
            pageId && isChatSidebarOpen && "md:basis-[calc(33%+166px)]",
          )}
        >
          {panelContent}
        </div>

        {isMobile && (
          <MobileSheet
            open={panelView !== "chat"}
            onOpenChange={(open) => {
              if (!open) {
                setPanelView("chat");
              }
            }}
            className="max-h-[85svh]"
          >
            {panelView === "history" && <VersionHistory />}
            {panelView === "designLibrary" && <DesignLibraryPanel />}
            {panelView === "pageSettings" && page && (
              <PageSettingsPanel page={page} />
            )}
          </MobileSheet>
        )}

        {pageId && (
          <div
            className={twMerge(
              "relative min-h-0 flex flex-col items-center w-full shadow-selectable border-0.5 border-border rounded-lg bg-white",
              "flex-1 flex-shrink-0 md:flex-none md:flex-shrink-0 transition-[flex-basis] duration-300 ease-in-out",
              !isChatSidebarOpen && "md:basis-[66%]",
              isChatSidebarOpen && "md:basis-[calc(66%-166px)]",
              panelView === "chat" &&
                isMobile &&
                effectiveMobileMode === "chat" &&
                "hidden",
            )}
          >
            {!isMobile && (
              <>
                {pageId && (
                  <PageRenderControls
                    page={page}
                    isLoading={isWaitingForPageData}
                  />
                )}
                <div className=" top-0 left-0 w-full relative">
                  <LoadingHeader key={pageId} />
                </div>
              </>
            )}

            <div
              className={twMerge("flex-1 relative w-full")}
              style={{
                maxWidth:
                  currentViewport !== "desktop"
                    ? `${CANVAS_DATA[currentViewport].defaultFrameWidth}px`
                    : undefined,
              }}
            >
              {page && (
                <div className="flex flex-col h-full w-full">
                  <ModifiablePageRenderer
                    pageRoute={page.pageRoute ?? ""}
                    className="rounded-t-none w-full rounded-b-lg"
                    isInteractive
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile: full-height chat container when in chat mode */}
        {panelView === "chat" && isMobile && effectiveMobileMode === "chat" && (
          <div className="flex-1 min-h-0 w-full md:hidden overflow-hidden">
            <ReactEditorChat />
          </div>
        )}

        {/* Mobile-only footer with Chat/Preview toggle and (in Preview) controls) */}
        {isMobile && (
          <MobileChatFooter
            pageId={canPreview ? pageId : null}
            page={page}
            isLoading={isWaitingForPageData}
            mode={effectiveMobileMode}
            onModeChange={(mode) => {
              if (mode === "preview" && !canPreview) {
                setMobileChatMode("chat");
                return;
              }
              setMobileChatMode(mode);
            }}
          />
        )}
      </div>
    </ModifiersProvider>
  );
}

export default ReactEditor;
